import { useQuery } from "@tanstack/react-query";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { ToolCallView } from "@/components/guardians/preview/ToolCallView";

export interface LazyToolCallStepDetailProps {
  interactionId: string;
  stepIndex: number;
}

/** Loads full tool-call step detail on demand for staff tooltips. */
export function LazyToolCallStepDetail({
  interactionId,
  stepIndex,
}: LazyToolCallStepDetailProps) {
  const { data: step, isLoading, isError } = useQuery({
    queryKey: ["agentic-v2", "interaction-step", interactionId, stepIndex],
    queryFn: async () => {
      const interaction = await AgenticV2API.getInteraction(interactionId);
      const loaded = interaction.steps?.[stepIndex];
      if (!loaded) {
        throw new Error("Step not found");
      }
      return loaded;
    },
    enabled: Boolean(interactionId) && stepIndex >= 0,
    retry: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <p className="text-xs text-slate-400" data-testid="tool-call-step-loading">
        Loading tool details…
      </p>
    );
  }
  if (isError || !step) {
    return (
      <p className="text-xs text-slate-500" data-testid="tool-call-step-error">
        Could not load tool details.
      </p>
    );
  }

  return (
    <div className="w-80 max-w-[92vw]" data-testid="tool-call-step-detail">
      <ToolCallView step={step} index={stepIndex} />
    </div>
  );
}
